/**
 * Utility functions for navigation
 */

/**
 * Generates a book reader URL from a book object
 * @param {object} book - The book object
 * @returns {string} - The reader URL
 */
export function getBookReaderUrl(book) {
  return `/reader/${book.slug}`;
}

/**
 * Prepares navigation state for book reader
 * @param {object} book - The book object
 * @returns {object} - Navigation state object
 */
export function getBookNavigationState(book) {
  return {
    from: getBookReaderUrl(book),
    bookData: book
  };
}
/**
 * Build the in-app path a notification should open, from the `target` the
 * notifications API resolves for it.
 *
 * Returns null for anything that can't be navigated to — an absent target, an
 * unrecognised kind, or a target missing an id the path needs. Callers treat
 * null as "render the row, but don't make it clickable": a notification that
 * predates targets, or whose shape nobody taught the reader about, must do
 * nothing rather than land somewhere wrong.
 *
 * @param {object|null} target - `{ kind, draftId, commentId?, versionId?, collabId?, goalId? }`
 * @returns {string|null} - path for react-router's navigate(), or null
 */
export function notificationPath(target) {
  if (!target) return null;

  switch (target.kind) {
    case 'manuscript_comment':
      if (!target.draftId || !target.commentId) return null;
      // The editor resolves ?comment= to its anchor block once comments load.
      return `/editor/${target.draftId}?comment=${encodeURIComponent(target.commentId)}`;

    case 'draft_review':
      // A review notice points at the draft itself — there's no finer anchor.
      if (!target.draftId) return null;
      return `/editor/${target.draftId}`;

    case 'cover_comment':
      if (!target.draftId || !target.versionId || !target.collabId) return null;
      return `/bookcover/${target.draftId}/designer/${target.versionId}`
        + `?collab=${encodeURIComponent(target.collabId)}`;

    case 'manuscript_goal':
      // No goalId when the goal was deleted or the reader was taken off it —
      // the manuscript itself is the most that can be opened then.
      if (!target.draftId) return null;
      return target.goalId
        ? `/editor/${target.draftId}?goal=${encodeURIComponent(target.goalId)}`
        : `/editor/${target.draftId}`;

    default:
      return null;
  }
}
